'use client';

import { useState, useCallback, useEffect } from 'react';
import AppLayout from '@/components/common/AppLayout';
import CalendarHeader from './CalendarHeader';
import CalendarGrid from './CalendarGrid';
import CrewSidebar from './CrewSidebar';
import BulkActionsToolbar from './BulkActionsToolbar';
import JobDetailModal from './JobDetailModal';
import FilterBar from './FilterBar';
import { createClient } from '@/lib/supabase/client';
import { getChatCompletion } from '@/lib/ai/chatCompletion';

export interface JobBlock {
  id: string;
  title: string;
  customer: string;
  address: string;
  crew: string;
  crewId: string;
  date: string;
  startHour: number;
  duration: number;
  status: 'scheduled' | 'in-progress' | 'completed' | 'delayed' | 'cancelled';
  priority: 'high' | 'medium' | 'low';
  projectType: string;
  laborCost: number;
  profitMargin: number;
  notes: string;
  endDate?: string | null;
}

export interface CrewMember {
  id: string;
  name: string;
  role: string;
  availability: 'available' | 'assigned' | 'off';
  skills: string[];
  currentJob: string | null;
  avatar: string;
  phone: string;
}

export interface WeatherData {
  condition: string;
  temp: string;
  icon: string;
  risk: 'low' | 'medium' | 'high';
}

export type ViewMode = 'month' | 'week' | 'day';

// Demo jobs removed — DB was wiped; empty schedule renders the zero state.
function buildDemoJobs(): JobBlock[] {
  return [];
}

const DEMO_CREW: CrewMember[] = [];

export default function CalendarInteractive() {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedJob, setSelectedJob] = useState<JobBlock | null>(null);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterCrew, setFilterCrew] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string[]>([]);
  const [jobs, setJobs] = useState<JobBlock[]>([]);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingCrew, setLoadingCrew] = useState(true);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [weatherMap, setWeatherMap] = useState<Record<string, WeatherData>>({});
  const [weatherFetched, setWeatherFetched] = useState(false);

  // Fetch jobs from roofing_jobs
  useEffect(() => {
    const supabase = createClient();
    const fetchJobs = async () => {
      setLoadingJobs(true);
      const { data, error } = await supabase
        .from('roofing_jobs')
        .select('job_id, customer_first_name, customer_last_name, address, city, state, status, install_date, end_date, contract_value, profit_margin, assigned_crew, notes, material')
        .order('install_date', { ascending: true })
        .limit(50);

      if (!error && data && data.length > 0) {
        const mapped: JobBlock[] = data
          .filter((row: any) => row.install_date)
          .map((row: any, idx: number) => {
            const customerName = `${row.customer_last_name || ''}`.trim() || 'Customer';
            const fullAddress = [row.address, row.city, row.state].filter(Boolean).join(', ');
            const rawStatus = row.status || 'scheduled';
            const mappedStatus: JobBlock['status'] =
              rawStatus === 'active' || rawStatus === 'in-progress' ? 'in-progress' :
              rawStatus === 'completed' ? 'completed' :
              rawStatus === 'cancelled' ? 'cancelled' :
              rawStatus === 'delayed' ? 'delayed' : 'scheduled';

            return {
              id: row.job_id || `job-${idx}`,
              title: row.material ? `${row.material} Installation` : 'Roofing Job',
              customer: customerName,
              address: fullAddress,
              crew: row.assigned_crew || 'Unassigned',
              crewId: '',
              date: row.install_date,
              startHour: 7 + (idx % 5),
              duration: 6,
              status: mappedStatus,
              priority: idx % 3 === 0 ? 'high' : idx % 3 === 1 ? 'medium' : 'low',
              projectType: row.material || 'Roofing',
              laborCost: row.contract_value ? Math.round(Number(row.contract_value) * 0.4) : 0,
              profitMargin: row.profit_margin ? Number(row.profit_margin) : 0,
              notes: row.notes || '',
              endDate: row.end_date ?? null,
            };
          });
        setJobs(mapped.length > 0 ? mapped : buildDemoJobs());
      } else {
        setJobs(buildDemoJobs());
      }
      setLoadingJobs(false);
    };
    fetchJobs();
  }, []);

  // Fetch crew from crew_members
  useEffect(() => {
    const supabase = createClient();
    const fetchCrew = async () => {
      setLoadingCrew(true);
      const { data, error } = await supabase
        .from('crew_members')
        .select('id, name, role, phone, avatar, avatar_alt, team, is_foreman, is_available')
        .order('name', { ascending: true });

      if (!error && data && data.length > 0) {
        const mapped: CrewMember[] = data.map((row: any) => ({
          id: row.id,
          name: row.name || 'Crew Member',
          role: row.role || (row.is_foreman ? 'Foreman' : 'Installer'),
          availability: row.is_available ? 'available' : 'assigned',
          skills: row.team ? [row.team] : ['Roofing'],
          currentJob: null,
          avatar: row.avatar || '',
          phone: row.phone || '',
        }));
        setCrew(mapped);
      } else {
        setCrew(DEMO_CREW);
      }
      setLoadingCrew(false);
    };
    fetchCrew();
  }, []);

  // Fetch weather via OpenAI for dates that have jobs
  const fetchWeather = useCallback(async (jobList: JobBlock[]) => {
    if (weatherFetched) return;
    setWeatherFetched(true);
    try {
      const uniqueDates = [...new Set(jobList.map(j => j.date))].slice(0, 7);
      if (uniqueDates.length === 0) return;

      const dateList = uniqueDates.join(', ');
      const response = await getChatCompletion(
        'GEMINI',
        'gemini/gemini-2.5-flash',
        [
          {
            role: 'system',
            content: 'You are a weather assistant. Return ONLY valid JSON, no markdown, no explanation.',
          },
          {
            role: 'user',
            content: `Generate realistic weather forecasts for roofing install days in a mid-US city for these dates: ${dateList}.
Return a JSON object with each date string as key. Each value: { condition: string (2-3 words), temp: string (e.g. "72°F"), icon: string (single weather emoji), risk: "low\"|\"medium\"|\"high" }.
Risk "high" = rain/storm/snow/wind >20mph. Risk "medium" = cloudy/light wind. Risk "low" = clear/sunny.
Example: {"2026-03-10": {"condition": "Partly Cloudy", "temp": "68°F", "icon": "⛅", "risk": "low"}}`,
          },
        ],
        { max_completion_tokens: 400 }
      );

      const content = response?.choices?.[0]?.message?.content ?? '';
      const parsed = JSON.parse(content);
      setWeatherMap(parsed as Record<string, WeatherData>);
    } catch {
      // Silently fail — weather is supplemental
    }
  }, [weatherFetched]);

  useEffect(() => {
    if (!loadingJobs && jobs.length > 0 && !weatherFetched) {
      fetchWeather(jobs);
    }
  }, [loadingJobs, jobs, weatherFetched, fetchWeather]);

  // Conflict detection: map of date → set of double-booked crew names
  const conflictMap: Record<string, Set<string>> = {};
  const dateCrewMap: Record<string, Record<string, number>> = {};
  jobs.forEach(j => {
    if (!j.crew || j.crew === 'Unassigned') return;
    if (!dateCrewMap[j.date]) dateCrewMap[j.date] = {};
    dateCrewMap[j.date][j.crew] = (dateCrewMap[j.date][j.crew] ?? 0) + 1;
  });
  Object.entries(dateCrewMap).forEach(([date, crewCounts]) => {
    const doubled = new Set(
      Object.entries(crewCounts).filter(([, count]) => count > 1).map(([crew]) => crew)
    );
    if (doubled.size > 0) conflictMap[date] = doubled;
  });

  const filteredJobs = jobs.filter(job => {
    if (filterStatus.length > 0 && !filterStatus.includes(job.status)) return false;
    if (filterCrew.length > 0 && !filterCrew.includes(job.crewId)) return false;
    if (filterType.length > 0 && !filterType.includes(job.projectType)) return false;
    return true;
  });

  const handleJobClick = useCallback((job: JobBlock) => {
    setSelectedJob(job);
  }, []);

  const handleJobSelect = useCallback((jobId: string) => {
    setSelectedJobs(prev => {
      const next = prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId];
      setShowBulkActions(next.length > 0);
      return next;
    });
  }, []);

  const handleBulkReschedule = useCallback((newDate: string) => {
    setJobs(prev => prev.map(j => selectedJobs.includes(j.id) ? { ...j, date: newDate, status: 'scheduled' } : j));
    setSelectedJobs([]);
    setShowBulkActions(false);
  }, [selectedJobs]);

  const handleBulkCancel = useCallback(() => {
    setJobs(prev => prev.map(j => selectedJobs.includes(j.id) ? { ...j, status: 'cancelled' } : j));
    setSelectedJobs([]);
    setShowBulkActions(false);
  }, [selectedJobs]);

  const handleNavigate = useCallback((direction: 'prev' | 'next' | 'today') => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (direction === 'today') return new Date();
      const delta = direction === 'next' ? 1 : -1;
      if (viewMode === 'day') d.setDate(d.getDate() + delta);
      else if (viewMode === 'week') d.setDate(d.getDate() + delta * 7);
      else d.setMonth(d.getMonth() + delta);
      return d;
    });
  }, [viewMode]);

  if (loadingJobs || loadingCrew) {
    return (
      <AppLayout>
        <div className="flex h-[calc(100vh-60px)] items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="font-body text-sm text-muted-foreground">Loading schedule...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex h-[calc(100vh-60px)] overflow-hidden bg-background">
        {/* Crew Sidebar */}
        <CrewSidebar crew={crew} />

        {/* Main Calendar Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <CalendarHeader
            viewMode={viewMode}
            currentDate={currentDate}
            onViewChange={setViewMode}
            onNavigate={handleNavigate}
          />
          <FilterBar
            crew={crew}
            filterStatus={filterStatus}
            filterCrew={filterCrew}
            filterType={filterType}
            onStatusChange={setFilterStatus}
            onCrewChange={setFilterCrew}
            onTypeChange={setFilterType}
            selectedCount={selectedJobs.length}
          />
          {showBulkActions && (
            <BulkActionsToolbar
              selectedCount={selectedJobs.length}
              onReschedule={handleBulkReschedule}
              onCancel={handleBulkCancel}
              onClear={() => { setSelectedJobs([]); setShowBulkActions(false); }}
            />
          )}
          <CalendarGrid
            viewMode={viewMode}
            currentDate={currentDate}
            jobs={filteredJobs}
            selectedJobs={selectedJobs}
            onJobClick={handleJobClick}
            onJobSelect={handleJobSelect}
            weatherMap={weatherMap}
            conflictMap={conflictMap}
          />
        </div>
      </div>

      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          crew={crew}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </AppLayout>
  );
}